import { FC, FocusEvent, KeyboardEvent, UIEvent, useId, useRef, useState } from 'react'
import { IconPaths, PrescricaoType } from '_metronic/helpers'
import { useAuth } from 'app/modules/auth'
import { alergiaCoincidente } from '../../hooks/useAlergiasPaciente'
import { useCatalogoBusca } from '../../hooks/useCatalogoBusca'
import { podeInativar } from '../../core/montagem/catalogo'
import { CATEGORIAS, CategoriaItem, ItemCatalogo, rotuloCatalogo } from '../../core/montagem/itens'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { useRotulos } from '../comum/useRotulos'
import { ResultadoBusca } from './ResultadoBusca'
import { Button } from 'app/components'

const CHAVE_CATEGORIA: Record<CategoriaItem, string> = {
  [PrescricaoType.Medicamento]: 'medicamento',
  [PrescricaoType.Formula]: 'formula',
  [PrescricaoType.Exame]: 'exame',
  [PrescricaoType.Orientacoes]: 'orientacao',
}

type IProps = {
  atendeSUS: boolean
  alergias: string[]
  jaAdicionado: (item: ItemCatalogo) => boolean
  onAdicionar: (item: ItemCatalogo) => void
  onCadastrar: (categoria: CategoriaItem) => void
}

/**
 * Busca sempre visível: abas de categoria, campo e painel de resultados. Teclado: ↓ do campo
 * entra na lista, ↑ ↓ andam entre os itens, Enter adiciona, Esc volta ao campo e fecha;
 * ← → trocam de aba. Cada resultado é um botão, então "Inativar" também é alcançável por Tab.
 */
export const BuscaPrescricao: FC<IProps> = ({
  atendeSUS,
  alergias,
  jaAdicionado,
  onAdicionar,
  onCadastrar,
}) => {
  const { t } = useRotulos()
  const { currentUser } = useAuth()
  const pessoaId = currentUser?.pessoa?.id
  const uid = useId()
  const [categoria, setCategoria] = useState<CategoriaItem>(PrescricaoType.Medicamento)
  const [termo, setTermo] = useState('')
  const [aberto, setAberto] = useState(false)
  const [inativado, setInativado] = useState<string>()
  const containerRef = useRef<HTMLDivElement>(null)
  const campoRef = useRef<HTMLInputElement>(null)
  const listaRef = useRef<HTMLUListElement>(null)

  const busca = useCatalogoBusca(categoria, termo, aberto)
  const chave = CHAVE_CATEGORIA[categoria]
  const idLista = `${uid}-resultados`
  const idStatus = `${uid}-status`

  const trocarCategoria = (nova: CategoriaItem) => {
    setCategoria(nova)
    setTermo('')
    setInativado(undefined)
  }

  const botoesDaLista = () =>
    Array.from(
      listaRef.current?.querySelectorAll<HTMLButtonElement>(`.${styles.acaoResultado}`) ?? []
    )

  const adicionar = (item: ItemCatalogo) => {
    if (jaAdicionado(item)) return
    onAdicionar(item)
    setTermo('')
    setAberto(false)
  }

  const teclaNoCampo = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'ArrowDown') {
      evento.preventDefault()
      setAberto(true)
      botoesDaLista()[0]?.focus()
    } else if (evento.key === 'Escape') {
      setAberto(false)
    }
  }

  const teclaNoResultado = (evento: KeyboardEvent<HTMLButtonElement>) => {
    const botoes = botoesDaLista()
    const atual = botoes.indexOf(evento.currentTarget)
    if (evento.key === 'ArrowDown') {
      evento.preventDefault()
      botoes[Math.min(atual + 1, botoes.length - 1)]?.focus()
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault()
      if (atual <= 0) campoRef.current?.focus()
      else botoes[atual - 1]?.focus()
    } else if (evento.key === 'Escape') {
      setAberto(false)
      campoRef.current?.focus()
    }
  }

  const teclaNaAba = (evento: KeyboardEvent<HTMLButtonElement>, indice: number) => {
    if (evento.key !== 'ArrowRight' && evento.key !== 'ArrowLeft') return
    const passo = evento.key === 'ArrowRight' ? 1 : CATEGORIAS.length - 1
    const proxima = (indice + passo) % CATEGORIAS.length
    trocarCategoria(CATEGORIAS[proxima])
    const abas =
      evento.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role=tab]')
    abas?.[proxima]?.focus()
  }

  // Fecha só quando o foco sai do bloco inteiro (campo, abas e painel).
  const aoSairDoBloco = (evento: FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(evento.relatedTarget as Node | null)) setAberto(false)
  }

  const aoRolar = (evento: UIEvent<HTMLUListElement>) => {
    const lista = evento.currentTarget
    if (
      busca.temMais &&
      !busca.carregandoMais &&
      lista.scrollTop + lista.clientHeight >= lista.scrollHeight - 40
    ) {
      busca.carregarMais()
    }
  }

  const status = busca.carregando
    ? t('prescricao.busca.buscando')
    : t('prescricao.busca.resultados', {
        quantidade: busca.resultados.length,
        categoria: t(`prescricao.categoria.${chave}.plural`),
      })

  return (
    <section className='card card-bordered mb-5' aria-labelledby={`${uid}-titulo`}>
      <div className='card-header align-items-center border-0 pt-5'>
        <h2 id={`${uid}-titulo`} className='card-title flex-column align-items-start m-0'>
          <span className='fs-4 fw-bold'>{t('prescricao.busca.titulo')}</span>
          <span className={`fs-7 fw-normal ${styles.textoSecundario}`}>
            {t('prescricao.busca.subtitulo')}
          </span>
        </h2>
      </div>
      <div
        className='card-body d-flex flex-column gap-3 position-relative pt-2'
        ref={containerRef}
        onBlur={aoSairDoBloco}
      >
        <div role='tablist' aria-label={t('prescricao.busca.categorias')} className={styles.abas}>
          {CATEGORIAS.map((item, indice) => (
            <Button
              key={item}
              id={`${uid}-aba-${CHAVE_CATEGORIA[item]}`}
              role='tab'
              ariaSelected={item === categoria}
              ariaControls={`${uid}-painel`}
              tabIndex={item === categoria ? 0 : -1}
              className={styles.aba}
              onClick={() => trocarCategoria(item)}
              onKeyDown={(evento) => teclaNaAba(evento, indice)}
              unstyled
            >
              <span aria-hidden className={styles.abaMarcador} />
              {t(`prescricao.categoria.${CHAVE_CATEGORIA[item]}.plural`)}
            </Button>
          ))}
        </div>

        <div id={`${uid}-painel`} role='tabpanel' aria-labelledby={`${uid}-aba-${chave}`}>
          <div className={styles.campoBusca}>
            <Icone icone={IconPaths.Search} className={`${styles.textoSecundario} fs-3`} />
            <input
              ref={campoRef}
              type='search'
              value={termo}
              onChange={(evento) => {
                setTermo(evento.target.value)
                setAberto(true)
              }}
              onFocus={() => setAberto(true)}
              onKeyDown={teclaNoCampo}
              placeholder={t(`prescricao.busca.placeholder.${chave}`)}
              aria-labelledby={`${uid}-titulo ${uid}-aba-${chave}`}
              aria-controls={idLista}
              aria-describedby={idStatus}
              autoComplete='off'
            />
          </div>

          <div id={idStatus} aria-live='polite' className='visually-hidden'>
            {aberto ? status : ''}
          </div>

          {aberto && (
            <div className={styles.painelBusca}>
              {busca.carregando && (
                <div className={`p-4 ${styles.textoSecundario}`} aria-busy>
                  {t('prescricao.busca.buscando')}
                </div>
              )}
              {!busca.carregando && busca.resultados.length === 0 && (
                <div className='p-4'>
                  {busca.termoBuscado
                    ? t('prescricao.busca.sem_resultado', {
                        termo: busca.termoBuscado,
                        categoria: t(`prescricao.categoria.${chave}.plural`).toLowerCase(),
                      })
                    : t('prescricao.busca.catalogo_vazio')}
                </div>
              )}
              {busca.resultados.length > 0 && (
                // Cabeçalho só visual: cada resultado já tem nome acessível completo no botão.
                <div aria-hidden className={styles.cabecalhoResultados}>
                  <span>{t(`prescricao.categoria.${chave}.plural`)}</span>
                  <span>
                    {categoria === PrescricaoType.Medicamento
                      ? t('prescricao.busca.coluna.classe')
                      : categoria === PrescricaoType.Exame
                      ? t('prescricao.busca.coluna.tipo')
                      : ''}
                  </span>
                  <span className='text-end'>{t('prescricao.busca.coluna.acao')}</span>
                </div>
              )}
              <ul id={idLista} ref={listaRef} className={styles.listaResultados} onScroll={aoRolar}>
                {busca.resultados.map((item) => (
                  <ResultadoBusca
                    key={`${item.categoria}-${item.id}`}
                    item={item}
                    atendeSUS={atendeSUS}
                    adicionado={jaAdicionado(item)}
                    proprio={!!item.criador && item.criador.id === pessoaId}
                    podeInativar={podeInativar(item, pessoaId)}
                    alergia={
                      item.categoria === PrescricaoType.Medicamento
                        ? alergiaCoincidente(item.nome, alergias)
                        : undefined
                    }
                    onAdicionar={() => adicionar(item)}
                    onInativar={async () => {
                      await busca.inativar(item)
                      setInativado(rotuloCatalogo(item))
                      campoRef.current?.focus()
                    }}
                    onTeclaNavegacao={teclaNoResultado}
                  />
                ))}
              </ul>
              {inativado && (
                <div role='status' className={`mx-4 mb-2 ${styles.avisoAcao}`}>
                  {t('prescricao.busca.inativado', { nome: inativado })}
                </div>
              )}
              <div className='d-flex justify-content-between align-items-center gap-3 px-4 py-3 border-top'>
                <span className={styles.textoSecundario}>
                  {t('prescricao.busca.nao_encontrou')}
                </span>
                <Button
                  icon={IconPaths.Add}
                  size='btn-sm'
                  className='btn-primary fw-bold'
                  onClick={() => {
                    setAberto(false)
                    onCadastrar(categoria)
                  }}
                >
                  {t(`prescricao.busca.cadastrar.${chave}`)}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
